import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

// Global Internal-Messenger notifier. Mounted app-wide (any page), so a new
// message pops up even when the user is NOT on the Messenger screen — just like
// WhatsApp: an in-app toast when the app is focused, a desktop/OS notification
// when the tab is in the background/minimized, plus a short chime. The chat you
// are currently viewing (while focused) is suppressed, so you aren't pinged for
// the conversation already open on screen.
const POLL_MS = 7000;

let _audioCtx;
function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    _audioCtx = _audioCtx || new Ctx();
    if (_audioCtx.state === 'suspended') _audioCtx.resume();
    const o = _audioCtx.createOscillator();
    const g = _audioCtx.createGain();
    o.connect(g);
    g.connect(_audioCtx.destination);
    o.type = 'sine';
    const t = _audioCtx.currentTime;
    o.frequency.setValueAtTime(880, t);
    o.frequency.setValueAtTime(1175, t + 0.12);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.25, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
    o.start(t);
    o.stop(t + 0.37);
  } catch (_) { /* audio not available */ }
}

export default function useMessageNotifications() {
  const unreadRef = useRef(null); // conv_id -> last-seen unread (null = first load)
  const timerRef = useRef(null);

  useEffect(() => {
    // Ask for desktop-notification permission once (best-effort).
    try {
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission().catch(() => {});
      }
    } catch (_) { /* ignore */ }

    // Browsers keep the AudioContext suspended until a user gesture, so unlock
    // it on the first interaction anywhere in the app — this makes the chime
    // reliable from the very first incoming message.
    const unlock = () => {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (Ctx) {
          _audioCtx = _audioCtx || new Ctx();
          if (_audioCtx.state === 'suspended') _audioCtx.resume();
        }
      } catch (_) { /* ignore */ }
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);

    const poll = async () => {
      if (!localStorage.getItem('session_token')) return;
      try {
        const res = await apiClient.get('/messenger/conversations');
        const convs = res.data.conversations || [];
        const prev = unreadRef.current;
        const next = {};
        convs.forEach((c) => { next[c.conversation_id] = c.unread_count || 0; });

        if (prev) {
          const focused = document.visibilityState === 'visible' && document.hasFocus();
          const openId = typeof window !== 'undefined' ? window.__openConversationId : null;
          convs.forEach((c) => {
            const before = prev[c.conversation_id] || 0;
            const now = c.unread_count || 0;
            if (now <= before) return;                           // nothing new
            if (focused && openId === c.conversation_id) return; // already viewing it

            const title = c.name || c.last_sender_name || 'New message';
            const body = c.last_message || 'You have a new message';
            playChime();

            const canDesktop = 'Notification' in window && Notification.permission === 'granted';
            if (!focused && canDesktop) {
              try {
                const n = new Notification(title, { body, tag: c.conversation_id, renotify: true });
                n.onclick = () => {
                  try { window.focus(); } catch (_) {}
                  window.location.href = `/messenger?conversation=${encodeURIComponent(c.conversation_id)}`;
                };
              } catch (_) {
                toast.info(`New message in ${title}`, { description: body });
              }
            } else {
              toast.info(`New message in ${title}`, { description: body });
            }
          });
        }
        unreadRef.current = next;
      } catch (_) { /* ignore transient errors */ }
    };

    poll();
    timerRef.current = setInterval(poll, POLL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);
}
